import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient, handleApiError } from '@/config/api';

/**
 * Redux state for the staff chat surface.
 *
 * Only the conversation list lives in Redux (from /chat/conversations).
 * Live message lists + Twilio Conversation objects stay outside the
 * store — the SDK objects aren't serializable and message updates are
 * already pushed by Twilio listeners in the ConversationScreen.
 */

export type ConversationType = 'GROUP' | 'DIRECT' | 'BROADCAST';

export interface ConversationMember {
  employeeId: number;
  fullName?: string;
  role: 'ADMIN' | 'MEMBER';
  channel: 'IN_APP' | 'SMS';
  muted: boolean;
}

export interface ConversationDto {
  id: number;
  twilioConversationSid: string;
  name?: string;
  type: ConversationType;
  location?: string;
  retentionDays?: number;
  archived: boolean;
  modifiedOn?: string;
  members: ConversationMember[];
}

export interface CreateConversationPayload {
  type: ConversationType;
  name?: string;
  memberEmployeeIds: number[];
}

interface ChatState {
  conversations: ConversationDto[];
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
}

const initialState: ChatState = {
  conversations: [],
  isLoading: false,
  isCreating: false,
  error: null,
};

export const getConversations = createAsyncThunk(
  'chat/getConversations',
  async (_, { rejectWithValue }) => {
    try {
      return await apiClient.get<ConversationDto[]>('/chat/conversations');
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const createConversation = createAsyncThunk(
  'chat/createConversation',
  async (payload: CreateConversationPayload, { rejectWithValue }) => {
    try {
      return await apiClient.post<ConversationDto>('/chat/conversations', payload);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getConversations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getConversations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.conversations = action.payload;
      })
      .addCase(getConversations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createConversation.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createConversation.fulfilled, (state, action) => {
        state.isCreating = false;
        // Prepend the new one so it shows at the top of the list.
        state.conversations = [action.payload, ...state.conversations];
      })
      .addCase(createConversation.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      });

    // Reset chat state on logout so the next signed-in user doesn't see
    // the previous identity's conversation list flash through. Matched by
    // action type string to avoid a circular import on authSlice.
    builder.addMatcher(
      (action: { type: string }) => action.type === 'auth/logout/fulfilled',
      () => initialState
    );
  },
});

export const { clearError } = chatSlice.actions;
export default chatSlice.reducer;
